import { addMinutes } from "date-fns";
import { PlanningModeEnum } from "shared/enums/globalEnums";

import { planningConfigurations } from "../config/planningConfigurations";
import { InitialData, PlanningConfig, SocRestorationPowerKeyEnum, TargetSocTypeEnum } from "../types";

const getTargetSoc = (
  targetType: TargetSocTypeEnum | null,
  soc_trial_1: number,
  soc_trial_3: number,
  soc_trial_2_4: number,
) => {
  switch (targetType) {
    case TargetSocTypeEnum.soc_trial_1:
      return soc_trial_1;
    case TargetSocTypeEnum.soc_trial_3:
      return soc_trial_3;
    case TargetSocTypeEnum.soc_trial_2_4:
      return soc_trial_2_4;
    case TargetSocTypeEnum.notApplicable:
      return 0.5;
    default:
      return "";
  }
};

const getInitialSocRestorationPower = (
  powerKey: SocRestorationPowerKeyEnum | null,
  initial_soc_restoration_1_3: number,
  initial_soc_restoration_2_4: number,
) => {
  switch (powerKey) {
    case SocRestorationPowerKeyEnum.soc_restoration_1_3:
      return initial_soc_restoration_1_3;
    case SocRestorationPowerKeyEnum.soc_restoration_2_4:
      return initial_soc_restoration_2_4;
    default:
      return "";
  }
};

const buildPlanningDefaults = (
  currentConfig: PlanningConfig,
  index: number,
  accumulator: Record<string, any>,
  initialData: InitialData,
) => {
  const {
    startingDate,
    soc_trial_1,
    soc_trial_3,
    soc_trial_2_4,
    fcr_engagement_default,
    initial_soc_restoration_1_3,
    initial_soc_restoration_2_4,
  } = initialData;

  const isInitialPlanning = index === 0;
  const previousConfig = index > 0 ? planningConfigurations[index - 1] : null;
  const previousEndDate = previousConfig ? accumulator[previousConfig.name].end_date : null;

  const start_date = isInitialPlanning ? new Date(startingDate) : previousEndDate;
  const end_date = addMinutes(start_date, currentConfig.durationInMinutes);

  return {
    active: true,
    start_date,
    end_date,
    mode: currentConfig.mode,
    chronicleFilename: currentConfig.chronicleFilename,
    reactivity_delay: currentConfig.reactivity_delay,
    targetSoc: getTargetSoc(currentConfig.target_soc_type, soc_trial_1, soc_trial_3, soc_trial_2_4),
    fcr_engagement: currentConfig.mode === PlanningModeEnum.soc_management ? "" : fcr_engagement_default,
    initial_soc_restoration_power: getInitialSocRestorationPower(
      currentConfig.soc_restoration_power_key,
      initial_soc_restoration_1_3,
      initial_soc_restoration_2_4,
    ),
  };
};

export const computeDefaultValues = (initialData: InitialData) => {
  return planningConfigurations.reduce<Record<string, any>>(
    (accumulator, currentConfig: PlanningConfig, index: number) => {
      return {
        ...accumulator,
        [currentConfig.name]: buildPlanningDefaults(currentConfig, index, accumulator, initialData),
      };
    },
    {},
  );
};
